import { Controller } from "@hotwired/stimulus"

// Debounced full-text search over the user's past conversations.
//
// While the field is empty it shows the regular chronological list
// (`defaultList` target); once the user types it hides that list and streams
// ranked results into the `results` target via a turbo-stream request to
// ConversationsController#search. Clearing the field restores the list.
export default class extends Controller {
  static targets = ["input", "results", "defaultList", "clear", "spinner", "field"]
  static values = {
    url: String,
    context: String,
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this._timer = null
    this._abort = null
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
    if (this._abort) this._abort.abort()
  }

  // Debounce keystrokes so we issue at most one request per pause.
  input() {
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => this.search(), this.debounceValue)
  }

  clear() {
    this.inputTarget.value = ""
    this.search()
    this.inputTarget.focus()
  }

  // Reveal/hide the search field. Focus it when opening; when closing, reset the
  // query so the chronological list comes back.
  toggleField() {
    if (!this.hasFieldTarget) return

    const nowHidden = this.fieldTarget.classList.toggle("hidden")
    if (nowHidden) {
      this.inputTarget.value = ""
      this.reset()
    } else {
      this.inputTarget.focus()
    }
  }

  search() {
    const query = this.query()
    if (query === "") {
      this.reset()
      return
    }
    this.showResults()
    this.request(query)
  }

  query() {
    return this.inputTarget.value.trim()
  }

  reset() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
    }
    if (this.hasDefaultListTarget) this.defaultListTarget.classList.remove("hidden")
    if (this.hasClearTarget) this.clearTarget.classList.add("hidden")
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
  }

  showResults() {
    if (this.hasResultsTarget) this.resultsTarget.classList.remove("hidden")
    if (this.hasDefaultListTarget) this.defaultListTarget.classList.add("hidden")
    if (this.hasClearTarget) this.clearTarget.classList.remove("hidden")
  }

  // Swap the clear button for a spinner while a request is in flight, so the
  // (noticeable) round-trip has visible feedback.
  showLoading() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden")
    if (this.hasClearTarget) this.clearTarget.classList.add("hidden")
  }

  hideLoading() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
    if (this.hasClearTarget && this.query() !== "") this.clearTarget.classList.remove("hidden")
  }

  // Abort any in-flight request before issuing a new one, so a slow response for
  // an earlier query can't land after (and overwrite) a fresher one.
  request(query) {
    if (this._abort) this._abort.abort()
    this._abort = new AbortController()
    this.showLoading()

    fetch(this.buildUrl(query), {
      headers: { Accept: "text/vnd.turbo-stream.html" },
      signal: this._abort.signal
    })
      .then((response) => response.text())
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
        this.hideLoading()
      })
      .catch((error) => {
        // A superseded request keeps the spinner up for the newer one in flight.
        if (error.name === "AbortError") return
        this.hideLoading()
        throw error
      })
  }

  buildUrl(query) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)
    if (this.contextValue) url.searchParams.set("context", this.contextValue)
    return url.toString()
  }
}
